import {
    IAlarmDefinitionChild,
    IIndicatorResponse,
    IIndicatorTimeSeriesData,
    IIndicatorTimeSeriesRequest,
    IIndicatorTimeSeriesResponse,
    MetricClassificationSourceEnum,
} from '@kcftech/prometheustypescriptapiclient';
import { SelectedSeriesPoint } from 'pages/workbench/types';
import { TrendIndicator } from 'pages/workbench/workbench_tabs/trend/useTrendIndicators';
import {
    TimeSeriesChartPaddingPx,
    TimeSeriesChartYAxisSizePx,
} from 'shared/chart/core/timeseries/shared';
import {
    ChartRange,
    LinePlotCurveStyle,
    TimeSeriesChartPin,
    TimeSeriesChartSeriesDataPoint,
    TimeSeriesData,
    TimeSeriesDataAxis,
    TimeSeriesDataPoint,
} from 'shared/chart/core/types';
import { isValueInChartRange } from 'shared/chart/shared/data';
import { ActivityLineColors, IndicatorThresholdColors } from 'styles/themes/KCFColor';
import { excludeNullAndUndefined, firstItem, lastItem, sortBy, toMap } from 'utils/array';
import { isRunningSpeed } from 'utils/indicator';
import { mapValue } from 'utils/number';
import { DateRange } from 'utils/types';

export const MaxTrends = 50;
export const MaxWaterfallPoints = 4;
export const TimeSeriesSliderPaddingPx = 5;
export const TimeSeriesYSliderWidthPx = 20;
export const TimeSeriesXSliderHeightPx = 20;
export const TimeSeriesYAxisWidthPx =
    TimeSeriesYSliderWidthPx +
    TimeSeriesSliderPaddingPx +
    TimeSeriesChartPaddingPx +
    TimeSeriesChartYAxisSizePx;

export const RemoveDataPointButtonTooltip = 'Remove Outlier Data Point';

const YAxisPlotPaddingPx = 15;
const MinReducedResolution = 0.15;
const StartReductionThreshold = 5;
const ReductionSlope = (MinReducedResolution - 1) / (MaxTrends - StartReductionThreshold);

export const TimeSeriesThresholdColors = {
    CriticalAlarm: IndicatorThresholdColors.criticalAlarm,
    Alarm: IndicatorThresholdColors.alarm,
    Warning: IndicatorThresholdColors.warning,
    OffThreshold: IndicatorThresholdColors.off,
};

export const dateRangesAreEqual = (range1: DateRange, range2: DateRange) =>
    range1.startDate === range2.startDate && range1.endDate === range2.endDate;

/**
 * Gets the max number of points for a given chart with number of requests.
 * @param numberOfRequests Number of requests
 * @param width Width of the graph
 */
export const calcTimeSeriesMaxPoints = (numberOfRequests: number, width = 1920) => {
    const reductionPercentage =
        1 - ReductionSlope * (StartReductionThreshold - numberOfRequests);
    const normalizedPercentage = Math.min(1, reductionPercentage);
    const resolution = Math.max(MinReducedResolution, normalizedPercentage);

    return Math.round(width * resolution);
};

const GForceUnit = 'g';
const MillimetersPerSecondSquaredUnit = 'mm/s²';
const InchesPerSecondUnit = 'in/sec';
const MillimetersPerSecond = 'mm/sec';
const SkewnessUnit = 'Skewness';
const CrestFactorUnit = 'Crest Factor';
const DamageUnit = 'Damage';

const UnknownPriority = 0;
const BasePriority = 65536;
const AccelerationPriority = BasePriority + 5;
const VelocityPriority = BasePriority + 4;
const SkewnessPriority = BasePriority + 3;
const CrestFactorPriority = BasePriority + 2;
const DamagePriority = BasePriority + 1;

export const UnknownIndicatorUnit = '?';

export const getUnitPriority = (indicatorUnits: string | undefined) => {
    if (!indicatorUnits || indicatorUnits === UnknownIndicatorUnit) {
        return UnknownPriority;
    }
    switch (indicatorUnits) {
        case GForceUnit:
        case MillimetersPerSecondSquaredUnit:
            return AccelerationPriority;
        case InchesPerSecondUnit:
        case MillimetersPerSecond:
            return VelocityPriority;
        case SkewnessUnit:
            return SkewnessPriority;
        case CrestFactorUnit:
            return CrestFactorPriority;
        case DamageUnit:
            return DamagePriority;
        default:
            return indicatorUnits.toLowerCase().charCodeAt(0);
    }
};

export const getTimeSeriesIndicatorUnits = (indicator: IIndicatorResponse | undefined) =>
    indicator?.DisplayUnits || UnknownIndicatorUnit;

export const getTimeSeriesChartPrimaryUnit = (
    trendIndicators: TrendIndicator[],
    queryData: IIndicatorTimeSeriesResponse[]
): string => {
    const indicatorDataExists = toMap(
        queryData,
        (indicator) => indicator.IndicatorId,
        (indicator) => indicator.IndicatorData.length > 0
    );
    const indicatorHasData = (indicatorId: string) =>
        indicatorDataExists.get(indicatorId) ?? false;

    // First we must sort by whether the indicators have data or not, then we sort by the
    // Unit priority. This allows us to not have indicators disappear if we happen to select
    // One with no data. See: BUG 59816
    return getTimeSeriesIndicatorUnits(
        firstItem(
            sortBy(
                trendIndicators,
                {
                    key: ({ indicatorId }) => Number(indicatorHasData(indicatorId)),
                    dir: 'desc',
                },
                {
                    key: ({ indicator }) =>
                        getUnitPriority(getTimeSeriesIndicatorUnits(indicator)),
                    dir: 'desc',
                }
            )
        )?.indicator
    );
};

export const getMinThresholdValue = ({
    HighAlarmValue,
    HighWarningValue,
    LowAlarmValue,
    LowWarningValue,
    OffThresholdValue,
}: IAlarmDefinitionChild) =>
    OffThresholdValue ??
    LowAlarmValue ??
    LowWarningValue ??
    HighWarningValue ??
    HighAlarmValue;

export const getMaxThresholdValue = ({
    HighAlarmValue,
    HighWarningValue,
    LowAlarmValue,
    LowWarningValue,
    OffThresholdValue,
}: IAlarmDefinitionChild) =>
    HighAlarmValue ??
    HighWarningValue ??
    LowWarningValue ??
    LowAlarmValue ??
    OffThresholdValue;

export type GetTimeSeriesChartYAxisRangeOptions = Readonly<{
    chartContentHeight: number;
    timeSeriesChartData: TimeSeriesData[];
    axis: TimeSeriesDataAxis;
    alarmDefinition: IAlarmDefinitionChild | undefined;
}>;

export const getTimeSeriesChartYAxisRange = ({
    chartContentHeight,
    timeSeriesChartData,
    axis,
    alarmDefinition,
}: GetTimeSeriesChartYAxisRangeOptions): ChartRange | undefined => {
    const axisData = timeSeriesChartData.filter(
        (data) => data.axis === axis && data.dataPoints.length > 0
    );

    if (axisData.length === 0) {
        return undefined;
    }

    const min = Math.min(...axisData.map((data) => data.seriesMinY));
    const max = Math.max(...axisData.map((data) => data.seriesMaxY));

    const minThresholdValue = alarmDefinition
        ? getMinThresholdValue(alarmDefinition)
        : undefined;

    const maxThresholdValue = alarmDefinition
        ? getMaxThresholdValue(alarmDefinition)
        : undefined;

    const finalMin =
        minThresholdValue !== undefined ? Math.min(min, minThresholdValue) : min;

    const finalMax =
        maxThresholdValue !== undefined ? Math.max(max, maxThresholdValue) : max;

    const difference = finalMax === finalMin ? finalMax : Math.abs(finalMax - finalMin);

    const padding = Math.abs(
        mapValue(YAxisPlotPaddingPx, 0, chartContentHeight, 0, difference)
    );

    return { min: finalMin - padding, max: finalMax + padding };
};

export const getTimeSeriesEventPoint = (
    selectedPoint: SelectedSeriesPoint | undefined,
    seriesPoints: TimeSeriesChartSeriesDataPoint[]
) => {
    if (!selectedPoint) {
        return firstItem(seriesPoints);
    }
    return (
        seriesPoints.find((point) => point.series.seriesId === selectedPoint.seriesId) ??
        firstItem(seriesPoints)
    );
};

export const toSeriesSelectedPoint = (
    seriesPoint: TimeSeriesChartSeriesDataPoint | undefined
): SelectedSeriesPoint | undefined =>
    seriesPoint
        ? {
              seriesId: seriesPoint.series.seriesId,
              xValue: seriesPoint.dataPoint.x,
              isDownsampled: seriesPoint.series.isDownsampled,
          }
        : undefined;

export const getTimeSeriesHoverPins = (
    timeSeriesData: TimeSeriesData[],
    point: SelectedSeriesPoint
): TimeSeriesChartPin[] =>
    timeSeriesData
        .map((series) => ({
            series,
            matchingPoints: series.dataPoints.filter(
                (dataPoint) => dataPoint.x === point.xValue
            ),
        }))
        .flatMap<TimeSeriesChartPin>(({ series, matchingPoints }) =>
            matchingPoints.map<TimeSeriesChartPin>((dataPoint) => ({
                pinId: `pin-${series.seriesId}-hover-${dataPoint.x}-${dataPoint.y}`,
                axis: series.axis,
                pinPoint: dataPoint,
                pinStyle: series.seriesId === point.seriesId ? 'dot' : 'speck',
                color: series.lineStyles.color,
            }))
        );

/**
 * Finds the closest point in the supplied `dataPoints`.  Returns `undefined` if the supplied `xValue`
 * less than the first data point or greater than the last data point.  This is needed when a time series
 * point is selected and the chart zoom changes, which can result in new data that does not contain the exact
 * same x value.
 */
export const findClosestTimeSeriesPoint = (
    dataPoints: TimeSeriesDataPoint[],
    xValue: number
): TimeSeriesDataPoint | undefined => {
    if (
        dataPoints.length === 0 ||
        xValue < dataPoints[0].x ||
        xValue > dataPoints[dataPoints.length - 1].x
    ) {
        return undefined;
    }

    const index = dataPoints.findIndex((data) => data.x >= xValue);

    if (index === -1) {
        return lastItem(dataPoints);
    }

    const point = dataPoints[index];
    const prevIndex = index - 1;

    if (point.x === xValue || !(prevIndex in dataPoints)) {
        return point;
    }

    const prevPoint = dataPoints[prevIndex];
    const pointDistance = Math.abs(point.x - xValue);
    const prevPointDistance = Math.abs(prevPoint.x - xValue);

    return pointDistance < prevPointDistance ? point : prevPoint;
};

export const getTimeSeriesSelectionPin = (
    timeSeriesData: TimeSeriesData[],
    point: SelectedSeriesPoint
): TimeSeriesChartPin | undefined => {
    const series = timeSeriesData.find((data) => data.seriesId === point.seriesId);

    if (!series) {
        return undefined;
    }

    const pinPoint = findClosestTimeSeriesPoint(series.dataPoints, point.xValue);

    if (!pinPoint) {
        return undefined;
    }

    return {
        pinId: `pin-${series.seriesId}-selection-${pinPoint.x}-${pinPoint.y}`,
        axis: series.axis,
        pinPoint,
        pinStyle: 'target',
        color: series.lineStyles.color,
        verticalCrosshairColor: ActivityLineColors.CreationMarker,
    };
};

export const getWaterfallPins = (
    timeSeriesData: TimeSeriesData[],
    waterfallFrequencyPoints: SelectedSeriesPoint[]
): TimeSeriesChartPin[] => {
    const matchingSeries = timeSeriesData.filter((data) =>
        waterfallFrequencyPoints.some((point) => point.seriesId === data.seriesId)
    );

    const seriesData = toMap(
        matchingSeries,
        (series) => series.seriesId,
        (series) => series
    );

    return excludeNullAndUndefined(
        waterfallFrequencyPoints.map<TimeSeriesChartPin | undefined>((waterfallPoint) => {
            const series = seriesData.get(waterfallPoint.seriesId);

            if (!series) {
                throw new Error(
                    `Missing series data for found point. Series id: ${waterfallPoint.seriesId}`
                );
            }

            const pinPoint = findClosestTimeSeriesPoint(
                series.dataPoints,
                waterfallPoint.xValue
            );
            if (!pinPoint) {
                return undefined;
            }

            return {
                pinId: `pin-${waterfallPoint.seriesId}-waterfall-${pinPoint.x}-${pinPoint.y}`,
                axis: series.axis,
                pinPoint,
                pinStyle: 'target',
                color: series.lineStyles.color,
            };
        })
    );
};

export const getIndicatorLineStyle = (
    indicator: IIndicatorResponse
): LinePlotCurveStyle =>
    isRunningSpeed(indicator.IndicatorType) ? 'stepAfter' : 'linear';

export type TransformTimeSeriesData = Readonly<{
    indicator: IIndicatorResponse;
    request: IIndicatorTimeSeriesRequest;
    data: IIndicatorTimeSeriesData[];
}>;

export const transformTimeSeriesData = ({
    indicator,
    request,
    data,
}: TransformTimeSeriesData) => {
    if (isRunningSpeed(indicator.IndicatorType)) {
        const lastDataPoint = lastItem(data);
        return lastDataPoint
            ? [...data, { ...lastDataPoint, X: request.EndDate.getTime() * 2 }]
            : data;
    }
    return data;
};

export const findTrendIndicator = (
    trendIndicators: TrendIndicator[] | undefined,
    indicatorId: string | undefined
) =>
    trendIndicators && indicatorId
        ? trendIndicators.find(
              (trendIndicator) => trendIndicator.indicatorId === indicatorId
          )
        : undefined;

export const findTimeSeriesData = (
    timeSeriesData: TimeSeriesData[] | undefined,
    indicatorId: string | undefined
) =>
    indicatorId && timeSeriesData
        ? timeSeriesData.find((data) => data.seriesId === indicatorId)
        : undefined;

export const indicatorHasThresholds = ({
    AlarmDefinition: alarmDefinition,
}: IIndicatorResponse) =>
    alarmDefinition !== undefined &&
    ((alarmDefinition.Classification !== MetricClassificationSourceEnum.Inherit &&
        alarmDefinition.OffThresholdValue !== undefined) ||
        alarmDefinition.CriticalLowAlarmValue !== undefined ||
        alarmDefinition.LowAlarmValue !== undefined ||
        alarmDefinition.LowWarningValue !== undefined ||
        alarmDefinition.HighWarningValue !== undefined ||
        alarmDefinition.HighAlarmValue !== undefined ||
        alarmDefinition.CriticalHighAlarmValue !== undefined);

export const findMostRecentPoint = (
    timeSeriesData: TimeSeriesData[],
    range: ChartRange
): SelectedSeriesPoint | undefined => {
    const mostRecentSeries =
        timeSeriesData.length > 0
            ? timeSeriesData.reduce((previousSeries, currentSeries) =>
                  previousSeries.dataPoints.length === 0 ||
                  (currentSeries.dataPoints.length > 0 &&
                      currentSeries.seriesMinY < previousSeries.seriesMinY)
                      ? currentSeries
                      : previousSeries
              )
            : undefined;

    return mostRecentSeries ? getLastPoint(mostRecentSeries, range) : undefined;
};

const getFirstPointInChartRange = (
    dataPoints: TimeSeriesDataPoint[],
    range: ChartRange
) => dataPoints.find((point) => isValueInChartRange(point.x, range));

const getLastPointInChartRange = (dataPoints: TimeSeriesDataPoint[], range: ChartRange) =>
    dataPoints.reverse().find((point) => isValueInChartRange(point.x, range));

export const getPreviousPoint = (
    selectedPoint: SelectedSeriesPoint,
    range: ChartRange,
    { dataPoints, seriesId, isDownsampled }: TimeSeriesData
): SelectedSeriesPoint => {
    const constrainedDataPoints = dataPoints.filter((point) =>
        isValueInChartRange(point.x, range)
    );
    const newIndex =
        constrainedDataPoints.findIndex((point) => point.x === selectedPoint.xValue) - 1;

    return newIndex < 0
        ? {
              xValue: constrainedDataPoints[constrainedDataPoints.length - 1].x,
              seriesId,
              isDownsampled,
          }
        : { xValue: constrainedDataPoints[newIndex].x, seriesId, isDownsampled };
};

export const getNextPoint = (
    selectedPoint: SelectedSeriesPoint,
    range: ChartRange,
    { dataPoints, seriesId, isDownsampled }: TimeSeriesData
): SelectedSeriesPoint => {
    const constrainedDataPoints = dataPoints.filter((point) =>
        isValueInChartRange(point.x, range)
    );
    const newIndex =
        constrainedDataPoints.findIndex((point) => point.x === selectedPoint.xValue) + 1;

    return newIndex > constrainedDataPoints.length - 1
        ? { xValue: constrainedDataPoints[0].x, seriesId, isDownsampled }
        : { xValue: constrainedDataPoints[newIndex].x, seriesId, isDownsampled };
};

const getClosestPointInSeries = (
    { xValue }: SelectedSeriesPoint,
    range: ChartRange,
    { dataPoints }: TimeSeriesData
) =>
    dataPoints.reduce((previous, current) => {
        const previousDistance = Math.abs(xValue - previous.x);
        const currentDistance = Math.abs(xValue - current.x);

        return currentDistance < previousDistance ||
            !isValueInChartRange(previous.x, range)
            ? current
            : previous;
    });

export const getLastPoint = (
    { dataPoints, seriesId, isDownsampled }: TimeSeriesData,
    range: ChartRange
) => {
    const lastPointInChartRange = getLastPointInChartRange(dataPoints, range);
    return lastPointInChartRange
        ? { xValue: lastPointInChartRange.x, seriesId, isDownsampled }
        : undefined;
};

export const getFirstPoint = (
    { dataPoints, seriesId }: TimeSeriesData,
    range: ChartRange
) => {
    const firstPointInChartRange = getFirstPointInChartRange(dataPoints, range);
    return firstPointInChartRange
        ? { xValue: firstPointInChartRange.x, seriesId, isDownsampled: false }
        : undefined;
};

export const getPreviousSeriesPoint = (
    selectedPoint: SelectedSeriesPoint | undefined,
    timeSeriesData: TimeSeriesData[],
    range: ChartRange
): SelectedSeriesPoint | undefined => {
    if (timeSeriesData.length <= 1) {
        return selectedPoint;
    }

    if (selectedPoint) {
        const seriesIndex =
            timeSeriesData.findIndex(
                (series) => series.seriesId === selectedPoint.seriesId
            ) - 1;

        const newIndex = seriesIndex < 0 ? timeSeriesData.length - 1 : seriesIndex;

        const series = timeSeriesData[newIndex];

        const closestPoint = getClosestPointInSeries(selectedPoint, range, series);

        return {
            xValue: closestPoint.x,
            seriesId: series.seriesId,
            isDownsampled: series.isDownsampled,
        };
    }

    return getLastPoint(timeSeriesData[0], range);
};

export const getNextSeriesPoint = (
    selectedPoint: SelectedSeriesPoint | undefined,
    timeSeriesData: TimeSeriesData[],
    range: ChartRange
): SelectedSeriesPoint | undefined => {
    if (timeSeriesData.length <= 1) {
        return selectedPoint;
    }

    if (selectedPoint) {
        const seriesIndex =
            timeSeriesData.findIndex(
                (series) => series.seriesId === selectedPoint.seriesId
            ) + 1;
        const newIndex = seriesIndex > timeSeriesData.length - 1 ? 0 : seriesIndex;

        const series = timeSeriesData[newIndex];

        const closestPoint = getClosestPointInSeries(selectedPoint, range, series);

        return {
            xValue: closestPoint.x,
            seriesId: series.seriesId,
            isDownsampled: series.isDownsampled,
        };
    }

    return getLastPoint(timeSeriesData[timeSeriesData.length - 1], range);
};
